import { memo, useMemo, useRef, useState } from 'react';

import Button from '@ui/buttons/button/button';
import Icon from '@ui/icons/icon';
import Text from '@ui/text/text/text';
import {
  TSessionFeatureListItem,
  ESessionFeatures,
  ToggleableSessionFeatures,
  SessionFeatureLabels,
} from '@/types/session-features.types';
import TooltipPortal from '@ui/tooltip-portal/tooltip-portal';
import Container from '@ui/container/container';
import { GENERAL_EDITABLE_PERMISSIONS } from '@/types/permissions.types';
import useHasPermission from '@/utils/authentication/use-has-permission';

type SessionFeaturesProps = {
  projectTypeDefaultFeatures: TSessionFeatureListItem[];
  selectedFeatures?: ESessionFeatures[];
  onChange?: (features: ESessionFeatures[], toggledFeature: ESessionFeatures) => void;
  hasCourse?: boolean;
};

const SessionFeatures = memo(
  ({ projectTypeDefaultFeatures, selectedFeatures = [], onChange, hasCourse }: SessionFeaturesProps) => {
    const tooltipTimer = useRef<NodeJS.Timeout | null>(null);
    const [showTooltip, setShowTooltip] = useState<ESessionFeatures | null>(null);
    const { hasPermission } = useHasPermission({ allowedRoles: GENERAL_EDITABLE_PERMISSIONS });

    const handleChange = (feature: ESessionFeatures) => {
      setShowTooltip(null);
      if (tooltipTimer.current) {
        clearTimeout(tooltipTimer.current);
      }
      const newSelectedFeatures = selectedFeatures.includes(feature)
        ? selectedFeatures.filter((f) => f !== feature)
        : [...selectedFeatures, feature];
      onChange?.(newSelectedFeatures, feature);
    };

    const selectedMap = useMemo(() => {
      const selectedMap = new Map<ESessionFeatures, boolean>();
      projectTypeDefaultFeatures.forEach((feature) => {
        selectedMap.set(feature.value, selectedFeatures.includes(feature.value));
      });

      return selectedMap;
    }, [projectTypeDefaultFeatures, selectedFeatures]);

    const handleShowTooltip = (feature: ESessionFeatures) => {
      if (tooltipTimer.current) {
        clearTimeout(tooltipTimer.current);
      }
      setShowTooltip(feature);
      tooltipTimer.current = setTimeout(() => {
        setShowTooltip(null);
      }, 4000);
    };

    const handleClearTooltip = () => {
      if (tooltipTimer.current) {
        clearTimeout(tooltipTimer.current);
      }
      setShowTooltip(null);
    };

    return (
      <Container className="flex-column gap-8">
        <Text className="L3" color="var(--gray-300)">
          Features
        </Text>
        <div className="flex fw-wrap gap-8">
          {projectTypeDefaultFeatures.map((feature) => {
            let isToggleAble: boolean = ToggleableSessionFeatures[feature.value] && hasPermission;
            if (feature.value === ESessionFeatures.COURSES) {
              isToggleAble = ToggleableSessionFeatures[feature.value] && (hasCourse ?? false) && hasPermission;
            }
            const isSelected = selectedMap.get(feature.value);
            const textColor = isSelected ? 'var(--gray-100)' : 'var(--gray-400)';
            let bgColor = isSelected ? 'var(--gray-900)' : 'var(--gray-1100)';

            const tooltipOpen = showTooltip === feature.value;
            if (tooltipOpen) {
              bgColor = 'var(--gray-800)';
            }

            return (
              <TooltipPortal
                disabled={isToggleAble}
                showOnFocus
                key={feature.value}
                open={tooltipOpen}
                animationType="slide-up"
                verticalOffset={-6}
                onClickAway={({ clickedTriggerElement }) => {
                  if (!clickedTriggerElement) {
                    handleClearTooltip();
                  }
                }}
                onTooltipClick={handleClearTooltip}
                onEscape={handleClearTooltip}
                onTab={handleClearTooltip}
                tooltipContent={
                  <Container className="C2" maxWidth="240px">
                    <Text color="var(--gray-true-dark-100)">
                      {SessionFeatureLabels[feature.value]} requires customized editing.
                    </Text>
                    <Text color="var(--gray-true-dark-100)">You may edit this feature in the session settings.</Text>
                  </Container>
                }
              >
                <Button
                  backgroundColor={bgColor}
                  hoverBackgroundColor={isToggleAble ? 'var(--gray-800)' : 'var(--gray-900)'}
                  onClick={() => {
                    if (isToggleAble) {
                      return handleChange(feature.value);
                    }
                    handleShowTooltip(feature.value);
                  }}
                  className={`S3 flex ai-center jc-flex-start gap-4 padding-h-6 padding-h-10 radius-8 ${!isToggleAble ? 'cursor-default' : ''}`}
                >
                  <Icon transitionDuration="50ms" name={feature.icon} size={16} color={textColor} />{' '}
                  <Text color={textColor}>{feature.label}</Text>
                </Button>
              </TooltipPortal>
            );
          })}
        </div>
      </Container>
    );
  }
);

export default SessionFeatures;
